<template>
  <div>
    <div class="h-1">
      <ProgressBar v-if="loading" />
    </div>
    <LeadDetail v-if="lead" :lead="lead" />
  </div>
</template>

<script setup lang="ts">
import type { ApolloError } from '@apollo/client/core'
import { computed, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import { useReviewsLeadQuery } from '@/gql/reviews'
import { ReviewsRouteName } from '@/router/types'
import ProgressBar from '@/ui/ProgressBar.vue'

import LeadDetail from './LeadDetail.vue'
import type { LeadDetailData, LeadsContext } from './types'

const props = defineProps<{
  leadId: string
  context: LeadsContext
}>()

// the page owns the card header, so it needs the lead itself for the header actions and the breadcrumb
const emit = defineEmits<{
  (e: 'update:lead', value: LeadDetailData | null | undefined): void
}>()

const route = useRoute()
const router = useRouter()

const variables = computed(() => ({ id: props.leadId }))

const { result, loading, onError } = useReviewsLeadQuery(variables, { clientId: 'reviews' })

const lead = computed(() => result.value?.manage?.lead)

watch(lead, (value) => emit('update:lead', value), { immediate: true })

function isNotFound(error: ApolloError): boolean {
  return error.graphQLErrors.some((graphQLError) => graphQLError.extensions?.code === 'NOT_FOUND')
}

onError((error) => {
  if (!isNotFound(error)) return
  router.replace({
    name: props.context === 'agency' ? ReviewsRouteName.ReviewsAgencyLeadsView : ReviewsRouteName.ReviewsProductLeads,
    params: props.context === 'agency' ? { agencyId: route.params.agencyId } : { productId: route.params.productId },
  })
})
</script>
